import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useMyConsents, useRevokeConsent } from '../../hooks/useOpenFinance';
import type { ConsentStatus } from '../../types/open-finance';

const statusColors: Record<ConsentStatus, 'success' | 'primary' | 'default' | 'error'> = {
  AWAITING_AUTHORIZATION: 'primary',
  AUTHORIZED: 'success',
  REJECTED: 'error',
  REVOKED: 'default',
  EXPIRED: 'default',
};

const statusLabels: Record<ConsentStatus, string> = {
  AWAITING_AUTHORIZATION: 'Aguardando',
  AUTHORIZED: 'Ativo',
  REJECTED: 'Rejeitado',
  REVOKED: 'Revogado',
  EXPIRED: 'Expirado',
};

const scopeLabels: Record<string, string> = {
  ACCOUNTS: 'Contas',
  TRANSACTIONS: 'Transações',
  CARDS: 'Cartões',
  CREDIT: 'Crédito',
};

export function ConsentDashboard() {
  const [statusFilter, setStatusFilter] = useState<ConsentStatus | undefined>();
  const { data: consentsData, isLoading } = useMyConsents({ status: statusFilter });
  const revokeConsent = useRevokeConsent();

  const handleRevoke = async (consentId: string) => {
    const reason = prompt('Motivo da revogação (opcional):');
    await revokeConsent.mutateAsync({ consentId, reason: reason || undefined });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const activeConsents = consentsData?.consents?.filter(c => c.status === 'AUTHORIZED') || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Meus Consentimentos</h1>
        <p className="text-gray-500">Gerencie os acessos autorizados aos seus dados</p>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{activeConsents.length}</div>
            <p className="text-sm text-gray-500">Consentimentos Ativos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{consentsData?.total || 0}</div>
            <p className="text-sm text-gray-500">Total de Consentimentos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">
              {new Set(activeConsents.map(c => c.tppId)).size}
            </div>
            <p className="text-sm text-gray-500">Instituições com Acesso</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={!statusFilter ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter(undefined)}
            >
              Todos
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'AUTHORIZED' ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter('AUTHORIZED')}
            >
              Ativos
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'REVOKED' ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter('REVOKED')}
            >
              Revogados
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'EXPIRED' ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter('EXPIRED')}
            >
              Expirados
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Consents List */}
      <Card>
        <CardHeader>
          <CardTitle>Consentimentos</CardTitle>
        </CardHeader>
        <CardContent>
          {consentsData?.consents && consentsData.consents.length > 0 ? (
            <div className="space-y-4">
              {consentsData.consents.map((consent) => (
                <div
                  key={consent.id}
                  className="p-4 border rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {consent.tppLogo && (
                        <img 
                          src={consent.tppLogo} 
                          alt={consent.tppName}
                          className="w-10 h-10 rounded"
                        />
                      )}
                      <div>
                        <h3 className="font-medium">{consent.tppName}</h3>
                        <p className="text-sm text-gray-500">
                          {consent.type === 'DATA_SHARING' 
                            ? 'Compartilhamento de Dados' 
                            : 'Iniciação de Pagamento'}
                        </p>
                      </div>
                    </div>
                    <Badge variant={statusColors[consent.status]}>
                      {statusLabels[consent.status]}
                    </Badge>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Dados Compartilhados:</p>
                    <div className="flex flex-wrap gap-2">
                      {consent.scopes.map((scope) => (
                        <Badge key={scope} variant="default">
                          {scopeLabels[scope] || scope}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div>
                      <span>Autorizado em: </span>
                      {consent.authorizedAt 
                        ? new Date(consent.authorizedAt).toLocaleDateString('pt-BR')
                        : '-'}
                    </div>
                    <div>
                      <span>Expira em: </span>
                      {new Date(consent.expiresAt).toLocaleDateString('pt-BR')}
                    </div>
                  </div>

                  {consent.status === 'AUTHORIZED' && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleRevoke(consent.id)}
                        disabled={revokeConsent.isPending}
                      >
                        Revogar Acesso
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum consentimento encontrado</p>
              <p className="text-sm text-gray-400 mt-2">
                Quando você autorizar instituições a acessar seus dados via Open Finance, 
                eles aparecerão aqui.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ConsentDashboard;
